/*
 * Bel Furniture accessibility patch for Easify Product Options dropdowns.
 *
 * Easify renders its custom dropdown as a readonly text input. This patch:
 * - exposes the trigger to screen readers as a collapsed/expanded combobox;
 * - lets Enter, Space, Alt+Down Arrow, and Down Arrow open it;
 * - lets Tab and Shift+Tab leave the control normally;
 * - keeps aria-expanded synchronized as closely as possible.
 *
 * This improves the trigger. The dropdown panel and its choices must still
 * expose accessible listbox/option semantics from Easify itself.
 */
(() => {
  if (window.__belEasifyNarratorFixInstalled) return;
  window.__belEasifyNarratorFixInstalled = true;

  const TRIGGER_SELECTOR =
    'input.tpo_option-input.tpo_option-dropdown[readonly]';

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(
      /([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g,
      '\\$1'
    );
  };

  const isVisible = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (!element.isConnected) return false;
    if (element.closest('[inert], [aria-hidden="true"]')) return false;
    if (element.matches(':disabled') || element.tabIndex < 0) return false;

    const style = window.getComputedStyle(element);

    if (
      style.display === 'none' ||
      style.visibility === 'hidden'
    ) {
      return false;
    }

    if (
      element instanceof HTMLInputElement &&
      element.type === 'radio' &&
      element.id
    ) {
      const label = document.querySelector(
        `label[for="${escapeSelector(element.id)}"]`
      );

      if (label && label.getClientRects().length > 0) {
        return true;
      }
    }

    return element.getClientRects().length > 0;
  };

  const getTabStops = () => {
    const candidates = Array.from(
      document.querySelectorAll(FOCUSABLE_SELECTOR)
    ).filter(isVisible);

    const radioGroups = new Map();
    const allowedRadios = new Set();

    candidates.forEach((element) => {
      if (
        !(element instanceof HTMLInputElement) ||
        element.type !== 'radio'
      ) {
        return;
      }

      const root =
        element.closest(
          'form, fieldset, variant-picker, .shopify-app-block'
        ) || document.body;

      const key = [
        element.name,
        root.id || '',
        root.tagName,
        root.dataset?.section || ''
      ].join('::');

      if (!radioGroups.has(key)) {
        radioGroups.set(key, []);
      }

      radioGroups.get(key).push(element);
    });

    radioGroups.forEach((radios) => {
      allowedRadios.add(
        radios.find((radio) => radio.checked) || radios[0]
      );
    });

    return candidates.filter((element) => {
      if (
        element instanceof HTMLInputElement &&
        element.type === 'radio'
      ) {
        return allowedRadios.has(element);
      }

      return true;
    });
  };

  const findAdjacentTabStop = (current, backwards) => {
    const tabStops = getTabStops();
    const currentIndex = tabStops.indexOf(current);

    if (currentIndex !== -1) {
      return tabStops[currentIndex + (backwards ? -1 : 1)] || null;
    }

    if (!backwards) {
      return tabStops.find((element) => Boolean(
        current.compareDocumentPosition(element) &
        Node.DOCUMENT_POSITION_FOLLOWING
      )) || null;
    }

    const previous = tabStops.filter((element) => Boolean(
      current.compareDocumentPosition(element) &
      Node.DOCUMENT_POSITION_PRECEDING
    ));

    return previous.length ? previous[previous.length - 1] : null;
  };

  const getAccessibleName = (trigger) => {
    return (
      trigger.getAttribute('aria-label') ||
      trigger.getAttribute('placeholder') ||
      trigger.name ||
      'Product option'
    );
  };

  const enhanceTrigger = (trigger) => {
    if (!(trigger instanceof HTMLInputElement)) return;
    if (trigger.dataset.belNarratorEnhanced === 'true') return;

    trigger.dataset.belNarratorEnhanced = 'true';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-readonly', 'true');
    trigger.setAttribute('aria-autocomplete', 'none');

    if (!trigger.hasAttribute('aria-label')) {
      trigger.setAttribute('aria-label', getAccessibleName(trigger));
    }
  };

  const enhanceAll = (root = document) => {
    root.querySelectorAll?.(TRIGGER_SELECTOR).forEach(enhanceTrigger);

    if (root instanceof Element && root.matches(TRIGGER_SELECTOR)) {
      enhanceTrigger(root);
    }
  };

  const setExpanded = (trigger, expanded) => {
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const openDropdown = (trigger) => {
    enhanceTrigger(trigger);
    trigger.focus({ preventScroll: true });

    /*
     * Easify opens the dropdown from its click handler. Dispatching a real
     * mouse event is more reliable than HTMLElement.click() for app widgets.
     */
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window
    });

    trigger.dispatchEvent(clickEvent);

    window.requestAnimationFrame(() => {
      setExpanded(trigger, true);
    });
  };

  window.addEventListener(
    'keydown',
    (event) => {
      const trigger =
        event.target instanceof Element
          ? event.target.closest(TRIGGER_SELECTOR)
          : null;

      if (!trigger) return;

      const opensDropdown =
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'ArrowDown' ||
        (
          event.altKey &&
          event.key === 'ArrowDown'
        );

      if (opensDropdown) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        openDropdown(trigger);
        return;
      }

      if (event.key === 'Escape') {
        setExpanded(trigger, false);
        return;
      }

      if (event.key !== 'Tab') return;

      const destination = findAdjacentTabStop(
        trigger,
        event.shiftKey
      );

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      setExpanded(trigger, false);
      trigger.blur();

      if (destination) {
        window.requestAnimationFrame(() => {
          if (destination.isConnected) {
            destination.focus({ preventScroll: true });
          }
        });
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest?.(TRIGGER_SELECTOR);

      document.querySelectorAll(TRIGGER_SELECTOR).forEach((item) => {
        if (item !== trigger) {
          setExpanded(item, false);
        }
      });
    },
    true
  );

  document.addEventListener(
    'focusout',
    (event) => {
      const trigger = event.target.closest?.(TRIGGER_SELECTOR);
      if (!trigger) return;

      window.setTimeout(() => {
        if (!trigger.contains(document.activeElement)) {
          setExpanded(trigger, false);
        }
      }, 0);
    },
    true
  );

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          enhanceAll(node);
        }
      });
    });
  });

  const start = () => {
    enhanceAll();

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
